import { useEffect, useState, useTransition } from 'react';
import { NEXT_ENTITY, EntityQueryResponseMap, QUERY_VARIANT } from '@common/interfaces';
import { getCachedToken } from '@config/authentication';
import { queryNextEntityWithToken } from '@utils/serverActions/clientUtility';
import { useAppSelector } from '@store/hooks';
import { selectOrderId } from '@store/selectors';

export interface IUseClientQueryOptions<MAPPED_ENTITY extends NEXT_ENTITY> {
  variables?: Record<string, any>;
  nextOptions?: Record<string, any>;
  watch?: unknown[];
  skip?: boolean;
  onCompleted?: (data: EntityQueryResponseMap[MAPPED_ENTITY]) => void;
  onError?: (error: any) => void;
  defaultLoading?: boolean;
}

export interface IUseClientQueryResult<MAPPED_ENTITY extends NEXT_ENTITY> {
  data: EntityQueryResponseMap[MAPPED_ENTITY] | null;
  loading: boolean;
  error: any;
}

export function useClientQuery<MAPPED_ENTITY extends NEXT_ENTITY>(
  entity: MAPPED_ENTITY,
  options: IUseClientQueryOptions<MAPPED_ENTITY> = {}
): IUseClientQueryResult<MAPPED_ENTITY> {
  const {
    variables = {},
    nextOptions = { cache: 'no-store' },
    watch = [],
    skip = false,
    onCompleted,
    onError,
    defaultLoading = false,
  } = options;
  const [isLoading, setIsLoading] = useState(defaultLoading);
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState<EntityQueryResponseMap[MAPPED_ENTITY] | null>(null);
  const [error, setError] = useState<any>(null);
  const nextOrderId = useAppSelector(selectOrderId);
  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return;
    }

    startTransition(() => {
      (async () => {
        try {
          setIsLoading(true);
          const result = await queryNextEntityWithToken(
            entity,
            getCachedToken(),
            variables,
            QUERY_VARIANT.FULL,
            nextOptions,
            nextOrderId
          );
          setData(result);
          setError(null);
          if (onCompleted) {
            onCompleted(result);
          }
        } catch (err) {
          setError(err);
          if (onError) {
            onError(err);
          }
        } finally {
          setIsLoading(false);
        }
      })();
    });
  }, [entity, JSON.stringify(variables), skip, JSON.stringify(watch)]);

  return {
    data,
    loading: isPending || isLoading,
    error,
  };
}
